import { useEffect } from 'react'
import { useView } from '../../contexts/ViewContext'
import { useData } from '../../contexts/DataContext'
import { useFilters } from '../../contexts/FilterContext'

function EducationFacilityModal() {
  const { showFacilityDetail, selectedFacilityId, hideFacilityDetail } =
    useView()
  const { facilities } = useData()
  const { activeCategory } = useFilters()

  // Find the selected facility from all facilities, not just filtered ones
  const allFacilities = facilities[activeCategory] || []
  const facility = allFacilities.find(f => f.id === selectedFacilityId)

  // Close modal on escape key and manage body overflow
  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape') {
        hideFacilityDetail()
      }
    }

    if (showFacilityDetail) {
      // Store original overflow value
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', handleEscape)

      return () => {
        document.removeEventListener('keydown', handleEscape)
        // Restore original overflow value
        document.body.style.overflow = originalOverflow
      }
    } else {
      // Ensure overflow is reset when modal is not showing
      document.body.style.overflow = ''
    }
  }, [showFacilityDetail, hideFacilityDetail])

  // Safety cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  if (!showFacilityDetail || !facility) {
    return null
  }

  // Helper functions for education data
  const hasDetailedData = () => {
    return (
      facility.total_learners ||
      facility.total_teachers ||
      facility.total_toilets
    )
  }

  const getInstitutionLevelColor = type => {
    switch (type) {
      case 'Primary':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'Secondary':
        return 'bg-green-100 text-green-800 border-green-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  // Infrastructure items available
  const infrastructureItems = [
    { id: 'electricity_available', name: 'Electricity', icon: '⚡' },
    { id: 'water_available', name: 'Water Supply', icon: '💧' },
    { id: 'ict_lab_available', name: 'ICT Laboratory', icon: '💻' },
    { id: 'handwashing_available', name: 'Handwashing Facilities', icon: '🧼' },
    {
      id: 'separate_gender_toilets',
      name: 'Gender-Separated Toilets',
      icon: '🚻',
    },
    {
      id: 'disability_accessible_toilets',
      name: 'Accessible Toilets',
      icon: '♿',
    },
    { id: 'teacher_toilets_available', name: 'Teacher Toilets', icon: '🚹' },
  ].filter(item => facility[item.id] === 'Yes')

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900">
              {facility.school_name || facility.name}
            </h2>
            <div className="flex items-center space-x-3 mt-2">
              <span
                className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getInstitutionLevelColor(facility.institution_type)}`}
              >
                {facility.institution_type}
              </span>
              <span className="text-sm text-gray-500">
                {facility.ownership}
              </span>
            </div>
          </div>
          <button
            onClick={hideFacilityDetail}
            className="text-gray-400 hover:text-gray-600 transition-colors text-2xl font-light ml-4"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {!hasDetailedData() ? (
            // Basic Info Only - for schools without detailed data
            <div className="text-center py-8">
              <div className="bg-blue-50 rounded-lg p-6 max-w-md mx-auto">
                <div className="text-4xl mb-4">🏫</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Basic Information Available
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  This school has basic registration information. Detailed
                  metrics are not available.
                </p>

                {/* Location */}
                <div className="text-left space-y-2 text-sm">
                  <div>
                    <span className="font-medium">District:</span>{' '}
                    {facility.district}
                  </div>
                  <div>
                    <span className="font-medium">Subcounty:</span>{' '}
                    {facility.subcounty}
                  </div>
                  {facility.parish && (
                    <div>
                      <span className="font-medium">Parish:</span>{' '}
                      {facility.parish}
                    </div>
                  )}
                  {facility.village && (
                    <div>
                      <span className="font-medium">Village:</span>{' '}
                      {facility.village}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            // Detailed Info - for schools with complete data
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Basic Info */}
              <div className="lg:col-span-1 space-y-6">
                {/* Location */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    📍 Location
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">District:</span>{' '}
                      {facility.district}
                    </div>
                    <div>
                      <span className="font-medium">Subcounty:</span>{' '}
                      {facility.subcounty}
                    </div>
                    {facility.parish && (
                      <div>
                        <span className="font-medium">Parish:</span>{' '}
                        {facility.parish}
                      </div>
                    )}
                    {facility.village && (
                      <div>
                        <span className="font-medium">Village:</span>{' '}
                        {facility.village}
                      </div>
                    )}
                  </div>
                </div>

                {/* Student Enrollment */}
                {facility.total_learners && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      👥 Student Enrollment
                    </h3>
                    <div className="space-y-3">
                      <div className="text-2xl font-semibold text-primary-600">
                        {facility.total_learners}
                      </div>
                      <div className="text-sm text-gray-500">
                        Total Students
                      </div>

                      {facility.boys_count && facility.girls_count && (
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="font-medium text-blue-600">
                              {facility.boys_count}
                            </div>
                            <div className="text-gray-500">Boys</div>
                          </div>
                          <div>
                            <div className="font-medium text-pink-600">
                              {facility.girls_count}
                            </div>
                            <div className="text-gray-500">Girls</div>
                          </div>
                        </div>
                      )}

                      {facility.disabled_learners > 0 && (
                        <div className="text-sm">
                          <span className="font-medium">
                            Students with Disabilities:
                          </span>{' '}
                          {facility.disabled_learners}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Teaching Staff */}
                {facility.total_teachers && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      👩‍🏫 Teaching Staff
                    </h3>
                    <div className="space-y-3">
                      <div className="text-2xl font-semibold text-green-600">
                        {facility.total_teachers}
                      </div>
                      <div className="text-sm text-gray-500">
                        Total Teachers
                      </div>

                      {facility.male_teachers && facility.female_teachers && (
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="font-medium text-blue-600">
                              {facility.male_teachers}
                            </div>
                            <div className="text-gray-500">Male</div>
                          </div>
                          <div>
                            <div className="font-medium text-pink-600">
                              {facility.female_teachers}
                            </div>
                            <div className="text-gray-500">Female</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Columns - Detailed Information */}
              <div className="lg:col-span-2 space-y-6">
                {/* Teacher Qualifications */}
                {(facility.diploma_teachers ||
                  facility.degree_teachers ||
                  facility.certificate_teachers) && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      🎓 Teacher Qualifications
                    </h3>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      {facility.degree_teachers > 0 && (
                        <div className="text-center">
                          <div className="text-xl font-semibold text-purple-600">
                            {facility.degree_teachers}
                          </div>
                          <div className="text-gray-500">Degree</div>
                        </div>
                      )}
                      {facility.diploma_teachers > 0 && (
                        <div className="text-center">
                          <div className="text-xl font-semibold text-blue-600">
                            {facility.diploma_teachers}
                          </div>
                          <div className="text-gray-500">Diploma</div>
                        </div>
                      )}
                      {facility.certificate_teachers > 0 && (
                        <div className="text-center">
                          <div className="text-xl font-semibold text-green-600">
                            {facility.certificate_teachers}
                          </div>
                          <div className="text-gray-500">Certificate</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Performance Ratios */}
                {(facility.pupil_teacher_ratio ||
                  facility.pupil_classroom_ratio) && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      📊 Performance Ratios
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {facility.pupil_teacher_ratio && (
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <div className="text-2xl font-semibold text-blue-600">
                            {facility.pupil_teacher_ratio}:1
                          </div>
                          <div className="text-sm text-gray-600">
                            Students per Teacher
                          </div>
                        </div>
                      )}
                      {facility.pupil_classroom_ratio && (
                        <div className="bg-green-50 p-4 rounded-lg">
                          <div className="text-2xl font-semibold text-green-600">
                            {facility.pupil_classroom_ratio}:1
                          </div>
                          <div className="text-sm text-gray-600">
                            Students per Classroom
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Infrastructure */}
                {infrastructureItems.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      🏢 Infrastructure & Facilities
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {infrastructureItems.map(item => (
                        <div
                          key={item.id}
                          className="flex items-center space-x-2 text-sm"
                        >
                          <span className="text-green-500">✅</span>
                          <span className="mr-1">{item.icon}</span>
                          <span>{item.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Classroom Information */}
                {facility.permanent_classrooms && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      🏫 Classroom Infrastructure
                    </h3>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-xl font-semibold text-green-600">
                          {facility.permanent_classrooms}
                        </div>
                        <div className="text-gray-500">Permanent</div>
                      </div>
                      {facility.semi_permanent_classrooms > 0 && (
                        <div className="text-center">
                          <div className="text-xl font-semibold text-orange-600">
                            {facility.semi_permanent_classrooms}
                          </div>
                          <div className="text-gray-500">Semi-Permanent</div>
                        </div>
                      )}
                      {facility.temporary_classrooms > 0 && (
                        <div className="text-center">
                          <div className="text-xl font-semibold text-red-600">
                            {facility.temporary_classrooms}
                          </div>
                          <div className="text-gray-500">Temporary</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="mt-8 pt-6 border-t border-surface-200 flex justify-end">
            <button onClick={hideFacilityDetail} className="btn btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EducationFacilityModal
